import type { MarkerDef } from './types'

/**
 * Marcadores laboratoriais da metodologia Mãe Nutrida para a SAÚDE DA MULHER.
 * Fonte: planilha `Exames Laboratoriais - Saúde da Mulher e Fertilidade.xlsx`.
 *
 * Esta mesma lista é reutilizada para TENTANTE — o briefing define que os
 * valores de referência seguem a planilha de saúde da mulher.
 */
export const MULHER_MARKERS: MarkerDef[] = [
  {
    id: 'hemacias',
    name: 'Hemácias',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { display: 'VR do laboratório' },
    },
  },
  {
    id: 'hemoglobina',
    name: 'Hemoglobina',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 11.5, max: 12.5, display: '11,5 – 12,5 g/dL', unit: 'g/dL' },
    },
  },
  {
    id: 'hematocrito',
    name: 'Hematócrito',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 39, max: 47, display: '39 - 47%', unit: '%' },
    },
  },
  {
    id: 'hcm',
    name: 'HCM',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 27, max: 33, display: '27 - 33pg', unit: 'pg' },
    },
  },
  {
    id: 'vcm',
    name: 'VCM',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 88, max: 92, display: '88 - 92fl', unit: 'fl' },
    },
  },
  {
    id: 'chcm',
    name: 'CHCM',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 31, max: 35, display: '31 - 35g/dL', unit: 'g/dL' },
    },
  },
  {
    id: 'rdw',
    name: 'RDW',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 11, max: 16, display: '11 – 16%', unit: '%' },
    },
  },
  {
    id: 'leucocitos',
    name: 'Leucócitos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 5, max: 7, display: '5 - 7mm³', unit: 'mm³' },
    },
  },
  {
    id: 'eosinofilos',
    name: 'Eosinófilos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { max: 1, display: '< 1%', unit: '%' },
    },
  },
  {
    id: 'linfocitos',
    name: 'Linfócitos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 25, max: 35, display: '25 - 35%', unit: '%' },
    },
  },
  {
    id: 'monocitos',
    name: 'Monócitos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 3, max: 8, display: '3 - 8 %', unit: '%' },
    },
  },
  {
    id: 'plaquetas',
    name: 'Plaquetas',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 250, max: 300, display: '250 mil - 300 mil' },
    },
  },
  {
    id: '25-odd3',
    name: '25 (OD)D3',
    group: 'Nutricional',
    ranges: {
      default: { min: 70, max: 100, display: '70 - 100ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'acido-folico',
    name: 'Ácido fólico',
    group: 'Nutricional',
    ranges: {
      default: { min: 10, max: 17, display: '10 - 17ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'folato-eritrocitario',
    name: 'Folato eritrocitário',
    group: 'Nutricional',
    ranges: {
      default: { min: 400, display: '> 400ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'vitamina-b12',
    name: 'Vitamina B12',
    group: 'Nutricional',
    ranges: {
      default: { min: 500, max: 800, display: '500 - 800 pg/mL', unit: 'pg/mL' },
    },
  },
  {
    id: 'acido-metilmalonico',
    name: 'Ácido Metilmalônico',
    group: 'Nutricional',
    ranges: {
      default: { max: 0.5, display: '< 0,5 mmol/L', unit: 'mmol/L' },
    },
  },
  {
    id: 'homocisteina',
    name: 'Homocisteína',
    group: 'Nutricional',
    ranges: {
      default: { max: 7, display: '< 7 mcmol/L', unit: 'mcmol/L' },
    },
  },
  {
    id: 'zinco-serico',
    name: 'Zinco sérico',
    group: 'Nutricional',
    ranges: {
      default: { min: 90, max: 110, display: '90 - 110 mcg/dL', unit: 'mcg/dL' },
    },
  },
  {
    id: 'cobre-serico',
    name: 'Cobre sérico',
    group: 'Nutricional',
    ranges: {
      default: { min: 90, max: 125, display: '90 a 125mcg/dL', unit: 'mcg/dL' },
    },
  },
  {
    id: 'calcio-ionico',
    name: 'Cálcio iônico',
    group: 'Nutricional',
    ranges: {
      default: { min: 1.1, max: 1.25, display: '1,1 a 1,25 mmol/L', unit: 'mmol/L' },
    },
  },
  {
    id: 'calcio-serico',
    name: 'Cálcio sérico',
    group: 'Nutricional',
    ranges: {
      default: { min: 2.28, max: 2.52, display: '2,28-2,52mmol/L', unit: 'mmol/L' },
    },
  },
  {
    id: 'magnesio-serico',
    name: 'Magnésio sérico',
    group: 'Nutricional',
    ranges: {
      default: { min: 2.2, display: '> 2,2 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'selenio-serico',
    name: 'Selênio sérico',
    group: 'Nutricional',
    ranges: {
      default: { min: 95, max: 190, display: '95 - 190 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'vitamina-c',
    name: 'Vitamina C',
    group: 'Nutricional',
    ranges: {
      default: { min: 1, display: '>1mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'vitamina-a',
    name: 'Vitamina A',
    group: 'Nutricional',
    ranges: {
      default: { display: '0,5mg/L', unit: 'mg/L' },
    },
  },
  {
    id: 'ferritina',
    name: 'Ferritina',
    group: 'Avaliação do ferro',
    obs: 'acompanhar com PCR',
    ranges: {
      default: { min: 70, display: '>70ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'saturacao-de-transferrina',
    name: 'Saturação de transferrina',
    group: 'Avaliação do ferro',
    ranges: {
      default: { min: 25, max: 35, display: '25-35%', unit: '%' },
    },
  },
  {
    id: 'ret-he',
    name: 'Ret-He',
    group: 'Avaliação do ferro',
    ranges: {
      default: { min: 29, display: '>29pg', unit: 'pg' },
    },
  },
  {
    id: 'pcrus',
    name: 'PCRus',
    group: 'Avaliação do ferro',
    ranges: {
      default: { max: 1, display: '< 1,0 mg/L', unit: 'mg/L' },
    },
  },
  {
    id: 'chumbo',
    name: 'Chumbo',
    group: 'Metais pesados',
    ranges: {
      default: { max: 3, display: '< 3,0 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'cadmio',
    name: 'Cádmio',
    group: 'Metais pesados',
    ranges: {
      default: { max: 0.5, display: '< 0,5 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'aluminio',
    name: 'Alumínio',
    group: 'Metais pesados',
    ranges: {
      default: { max: 2, display: '< 2,0 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'mercurio',
    name: 'Mercúrio',
    group: 'Metais pesados',
    ranges: {
      default: { max: 1, display: '< 1,0 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'arsenico',
    name: 'Arsênico',
    group: 'Metais pesados',
    obs: 'ideal < 5µU/mL',
    ranges: {
      default: { max: 10, display: '< 10,0 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'glicemia-jejum',
    name: 'Glicemia jejum',
    group: 'Glicêmico',
    obs: '2h após almoço',
    ranges: {
      default: { min: 70, max: 85, display: '70 - 85mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'insulina-jejum',
    name: 'Insulina jejum',
    group: 'Glicêmico',
    obs: '2h após almoço',
    ranges: {
      default: { max: 10, display: '< 10µU/mL', unit: 'µU/mL' },
    },
  },
  {
    id: 'glicemia-pos-prandial',
    name: 'Glicemia pós prandial',
    group: 'Glicêmico',
    ranges: {
      default: { max: 120, display: '< 120mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'insulina-pos-prandial',
    name: 'Insulina pós prandial',
    group: 'Glicêmico',
    ranges: {
      default: { max: 10, display: '< 10µU/mL', unit: 'µU/mL' },
    },
  },
  {
    id: 'hemoglobina-glicada',
    name: 'Hemoglobina glicada',
    group: 'Glicêmico',
    obs: 'Razão LH/FSH ideal até 2',
    ranges: {
      default: { max: 5, display: '< 5%', unit: '%' },
    },
  },
  {
    id: 'fsh',
    name: 'FSH (3° dia ciclo)',
    group: 'Hormonal',
    ranges: {
      default: { max: 10, display: '< 10UI/L', unit: 'UI/L' },
    },
  },
  {
    id: 'estradiol',
    name: 'Estradiol (3° dia ciclo)',
    group: 'Hormonal',
    obs: 'excesso > 50',
    ranges: {
      default: { min: 40, max: 50, display: '40 - 50 pg/mL', unit: 'pg/mL' },
    },
  },
  {
    id: 'shbg',
    name: 'SHBG',
    group: 'Hormonal',
    ranges: {
      default: { max: 30, display: '< 30nmol/L', unit: 'nmol/L' },
    },
  },
  {
    id: 'progesterona-21-ao-23-dia-do-ciclo-ou-8-dias-apos-ovulacao',
    name: 'Progesterona (21° ao 23° dia do ciclo) ou 8 dias após ovulação',
    group: 'Hormonal',
    obs: 'atenção se val. muito altos. Avaliar idade da paciente.',
    ranges: {
      default: { min: 10, display: '>10ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'inibina-b',
    name: 'Inibina B (fase folicular média)',
    group: 'Hormonal',
    ranges: {
      default: { min: 125, max: 150, display: '125 a 150pg/ml', unit: 'pg/ml' },
    },
  },
  {
    id: 'amh',
    name: 'AMH',
    group: 'Hormonal',
    ranges: {
      default: { min: 1.1, display: '>1,1ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'prolactina',
    name: 'Prolactina',
    group: 'Hormonal',
    ranges: {
      default: { max: 15, display: '<15ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 's-dhea',
    name: 'S-DHEA',
    group: 'Hormonal',
    ranges: {
      default: { min: 200, display: '> 200mcg/dL', unit: 'mcg/dL' },
    },
  },
  {
    id: 'testosterona-total',
    name: 'Testosterona total',
    group: 'Hormonal',
    ranges: {
      default: { min: 20, max: 50, display: '20 – 50ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'testosterona-livre',
    name: 'Testosterona livre',
    group: 'Hormonal',
    ranges: {
      default: { min: 6, max: 8.5, display: '6 – 8,5 ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'cortisol',
    name: 'Cortisol (até 8:30h)',
    group: 'Hormonal',
    ranges: {
      default: { min: 15, max: 25, display: '15 - 25 mcg/dL', unit: 'mcg/dL' },
    },
  },
  {
    id: 'melatonina-salivar',
    name: 'Melatonina salivar',
    group: 'Hormonal',
    ranges: {
      default: { min: 10, display: '> 10pg/mL', unit: 'pg/mL' },
    },
  },
  {
    id: 'tsh',
    name: 'TSH',
    group: 'Tireoide',
    ranges: {
      default: { max: 2, display: 'até 2,0 UI/mL', unit: 'UI/mL' },
    },
  },
  {
    id: 't3-livre',
    name: 'T3 livre',
    group: 'Tireoide',
    ranges: {
      default: { min: 0.32, display: '> 0,32ng/dL', unit: 'ng/dL' },
    },
  },
  {
    id: 't4-livre',
    name: 'T4 livre',
    group: 'Tireoide',
    ranges: {
      default: { min: 1.1, display: '> 1,1ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 't3-reverso',
    name: 'T3 reverso',
    group: 'Tireoide',
    ranges: {
      default: { min: 0.1, max: 0.25, display: '0,1 - 0,25ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'anti-tireoglobulina',
    name: 'Anti tireoglobulina',
    group: 'Tireoide',
    obs: 'o importante é ser < que o VR',
    ranges: {
      default: { display: '< VR' },
    },
  },
  {
    id: 'anti-tireoperoxidase',
    name: 'Anti tireoperoxidase',
    group: 'Tireoide',
    ranges: {
      default: { display: '< VR' },
    },
  },
  {
    id: 'pth',
    name: 'PTH',
    group: 'Tireoide',
    ranges: {
      default: { min: 15, max: 20, display: '15 – 20pg/mL', unit: 'pg/mL' },
    },
  },
  {
    id: 'iodo-urinario-24h',
    name: 'Iodo urinário 24h',
    group: 'Tireoide',
    ranges: {
      default: { min: 150, max: 249, display: '150 – 249ug/L', unit: 'g/L' },
    },
  },
  {
    id: 'colesterol-total',
    name: 'Colesterol total',
    group: 'Lipídico, metabólico e hepático',
    ranges: {
      default: { max: 200, display: '< 200 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'colesterol-hdl',
    name: 'Colesterol HDL',
    group: 'Lipídico, metabólico e hepático',
    ranges: {
      default: { min: 60, max: 93, display: '60 - 93 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'colesterol-ldl',
    name: 'Colesterol LDL',
    group: 'Lipídico, metabólico e hepático',
    ranges: {
      default: { max: 100, display: '< 100 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'nao-hdl',
    name: 'Não HDL',
    group: 'Lipídico, metabólico e hepático',
    ranges: {
      default: { max: 130, display: '< 130 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'triglicerideos',
    name: 'Triglicerídeos',
    group: 'Lipídico, metabólico e hepático',
    ranges: {
      default: { max: 100, display: '< 100 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'acido-urico',
    name: 'Ácido úrico',
    group: 'Lipídico, metabólico e hepático',
    ranges: {
      default: { max: 3.9, display: '< 3,9 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'tgo',
    name: 'TGO',
    group: 'Lipídico, metabólico e hepático',
    ranges: {
      default: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
    },
  },
  {
    id: 'tgp',
    name: 'TGP',
    group: 'Lipídico, metabólico e hepático',
    ranges: {
      default: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
    },
  },
  {
    id: 'ggt',
    name: 'GGT',
    group: 'Lipídico, metabólico e hepático',
    ranges: {
      default: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
    },
  },
]
